/* eslint-disable eslint-comments/no-restricted-disable -- Need to disable for now */

/* eslint-disable sonarjs/cognitive-complexity -- Not much to refactor at the moment */
import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import mergeRefs from "merge-refs";
import { useDeviceContext } from "@/react/components/shared/BuilderUI/DeviceProvider";
import {
  useBuilderStore,
  useSelectedBlock,
} from "@circle-react-shared/BuilderUI";
import type {
  BlockRendererMapping,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  isChildrenProp,
  isRootBlock,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { isNonNullableUnknown } from "../Helpers/typePredicates";
import { PreviewBlockHover } from "./PreviewBlockHover";
import { PreviewBlockList } from "./PreviewBlockList";
import { flattenResponsiveStylesDeep } from "./flattenResponsiveStylesDeep";
import { getAcceptsRejectsConfig } from "./getAcceptsRejectsConfig";

export interface PreviewProps extends ComponentPropsWithRef<"div"> {
  block: BaseBlock;
  components: BlockRendererMapping;
  path: string;
  shouldShowBlockHover?: boolean;
}

export const Preview = forwardRef<HTMLElement, PreviewProps>(
  (
    {
      block,
      components,
      path,
      className,
      shouldShowBlockHover = true,
      ...externalProps
    },
    forwardedRef,
  ) => {
    const scrollRef = useRef<HTMLElement>(null);
    const hoveredElementRef = useRef<HTMLElement | null>(null);
    const { setSelectedBlockPath, selectedBlock } = useSelectedBlock();

    const [isHovering, setIsHovering] = useState(false);
    const { isOver, over, active } = useDroppable({ id: block.id });

    const isDraggedOver =
      active && (isOver || over?.data?.current?.parentBlockId === block.id);

    const { getBlockMeta, closeSidebarView } = useBuilderStore(state => ({
      getBlockMeta: state.getBlockMeta,
      closeSidebarView: state.closeSidebarView,
    }));

    const selectedBlockId = selectedBlock?.id;
    const device = useDeviceContext();
    const currentBlockId = block.id;
    const externalOnClick = externalProps.onClick;
    const isCurrentBlockSelected = useMemo(
      () => selectedBlockId === currentBlockId,
      [selectedBlockId, currentBlockId],
    );

    const handleClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        externalOnClick?.(event);
        event.stopPropagation();
        setSelectedBlockPath(path);
        closeSidebarView();
      },
      [externalOnClick, path, setSelectedBlockPath, closeSidebarView],
    );

    const onMouseOver = useCallback((event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setIsHovering(true);
      hoveredElementRef.current = event.currentTarget as HTMLElement;
    }, []);

    const onMouseOut = useCallback((event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setIsHovering(false);
      hoveredElementRef.current = null;
    }, []);

    const PreviewComponent = useMemo(
      () => components[block.type],
      [components, block.type],
    );

    const blockMeta = useMemo(
      () => getBlockMeta(block.type),
      [getBlockMeta, block.type],
    );

    const shouldNotAddEvents = useMemo(() => isRootBlock(block), [block]);

    const blockProps = useMemo(() => {
      if (!blockMeta) return {};

      const entries = Object.entries(block.props);
      return entries.reduce<BlockValues>((props, [name, prop]) => {
        if (isChildrenProp(prop)) {
          const { accepts, rejects } = getAcceptsRejectsConfig(blockMeta, name);

          props[name] = (
            <PreviewBlockList
              accepts={accepts}
              rejects={rejects}
              blocks={prop.value}
              components={components}
              path={`${path}.props.${name}.value`}
              shouldShowBlockHover={shouldShowBlockHover}
            />
          );
        } else if (isNonNullableUnknown(prop.value)) {
          if (name === "styles") {
            props[name] = flattenResponsiveStylesDeep(prop.value, device);
          } else {
            props[name] = prop.value;
          }
        }
        return props;
      }, {});
    }, [
      block.props,
      blockMeta,
      components,
      path,
      shouldShowBlockHover,
      device,
    ]);

    const shouldShowMouseHover = useMemo(
      () => shouldShowBlockHover && isHovering && !isCurrentBlockSelected,
      [shouldShowBlockHover, isHovering, isCurrentBlockSelected],
    );

    const shouldShowDragHover = useMemo(
      () => shouldShowBlockHover && isDraggedOver && !isCurrentBlockSelected,
      [shouldShowBlockHover, isDraggedOver, isCurrentBlockSelected],
    );

    if (!PreviewComponent || !blockMeta) return null;

    const ref = mergeRefs(scrollRef, forwardedRef);

    if (shouldNotAddEvents) {
      return (
        <PreviewComponent
          data-preview-mode
          data-block
          data-block-type={block.type}
          {...blockProps}
        />
      );
    }

    return (
      <>
        <PreviewComponent
          {...blockProps}
          {...externalProps}
          blockPath={path}
          key={currentBlockId}
          id={currentBlockId}
          ref={ref}
          data-preview-mode
          data-block
          data-block-type={block.type}
          data-block-path={path}
          onClick={handleClick}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          className={className}
        />
        {shouldShowMouseHover && hoveredElementRef.current && (
          <PreviewBlockHover
            blockMeta={blockMeta}
            hoveredElement={hoveredElementRef.current}
          />
        )}
        {shouldShowDragHover && scrollRef.current && (
          <PreviewBlockHover
            blockMeta={blockMeta}
            hoveredElement={scrollRef.current}
          />
        )}
      </>
    );
  },
);
Preview.displayName = "Preview";
